import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, Search, X, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProducts = () => {
  const { token } = useAuth();
  const { refreshStats } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    title_es: '',
    slug: '',
    category: '',
    category_es: '',
    description: '',
    description_es: '',
    outcome: '',
    outcome_es: '',
    whats_inside: '',
    whats_inside_es: '',
    who_its_for: '',
    who_its_for_es: '',
    format: '',
    format_es: '',
    price: '',
    featured_image: '',
    active: true
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      toast.error('Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: editingProduct ? prev.slug : generateSlug(title)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      price: parseFloat(formData.price),
      whats_inside: formData.whats_inside.split('\n').filter(item => item.trim()),
      whats_inside_es: formData.whats_inside_es ? formData.whats_inside_es.split('\n').filter(item => item.trim()) : null
    };

    try {
      const url = editingProduct 
        ? `${API_URL}/api/admin/products/${editingProduct.id}`
        : `${API_URL}/api/admin/products`;
      
      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(editingProduct ? 'Product updated!' : 'Product created!');
        setShowModal(false);
        resetForm();
        fetchProducts();
        refreshStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error saving product');
      }
    } catch (error) {
      toast.error('Error saving product');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Product deleted!');
        fetchProducts();
        refreshStats();
      }
    } catch (error) {
      toast.error('Error deleting product');
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      title_es: product.title_es || '',
      slug: product.slug,
      category: product.category,
      category_es: product.category_es || '',
      description: product.description,
      description_es: product.description_es || '',
      outcome: product.outcome,
      outcome_es: product.outcome_es || '',
      whats_inside: product.whats_inside.join('\n'),
      whats_inside_es: product.whats_inside_es ? product.whats_inside_es.join('\n') : '',
      who_its_for: product.who_its_for,
      who_its_for_es: product.who_its_for_es || '',
      format: product.format,
      format_es: product.format_es || '',
      price: product.price.toString(),
      featured_image: product.featured_image || '',
      active: product.active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      title: '',
      title_es: '',
      slug: '',
      category: '',
      category_es: '',
      description: '',
      description_es: '',
      outcome: '',
      outcome_es: '',
      whats_inside: '',
      whats_inside_es: '',
      who_its_for: '',
      who_its_for_es: '',
      format: '',
      format_es: '',
      price: '',
      featured_image: '',
      active: true
    });
  };

  const filteredProducts = products.filter(product => 
    product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-terracota border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-products">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-serif text-white mb-1">Products</h1>
          <p className="text-stone-400 text-sm">{products.length} total products</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-terracota text-white px-4 py-2 rounded-lg hover:bg-terracota/90 transition-colors"
          data-testid="add-product-btn"
        >
          <Plus className="w-5 h-5" />
          New Product
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-stone-800 border border-stone-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota"
        />
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProducts.length === 0 ? (
          <div className="col-span-full bg-stone-800 rounded-xl p-8 text-center border border-stone-700">
            <p className="text-stone-400">No products found</p>
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div 
              key={product.id}
              className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden"
            >
              {product.featured_image && (
                <div className="h-32 overflow-hidden">
                  <img 
                    src={product.featured_image} 
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-xs text-terracota">{product.category}</span>
                    <h3 className="text-white font-medium">{product.title}</h3>
                  </div>
                  {!product.active && (
                    <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">Inactive</span>
                  )}
                </div>
                <p className="text-stone-400 text-sm line-clamp-2 mb-3">{product.outcome}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-white font-semibold">
                    <DollarSign className="w-4 h-4" />
                    {product.price.toFixed(2)}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditModal(product)}
                      className="p-2 text-stone-400 hover:text-white transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="p-2 text-stone-400 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-stone-800 rounded-xl w-full max-w-4xl my-8 border border-stone-700">
            <div className="flex items-center justify-between p-4 border-b border-stone-700">
              <h2 className="text-lg font-semibold text-white">
                {editingProduct ? 'Edit Product' : 'New Product'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-stone-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Title (EN) *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Title (ES)</label>
                  <input
                    type="text"
                    value={formData.title_es}
                    onChange={(e) => setFormData({ ...formData, title_es: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Category (EN) *</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Price (USD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Outcome (EN) *</label>
                  <input
                    type="text"
                    value={formData.outcome}
                    onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    placeholder="What will customers achieve?"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Outcome (ES)</label>
                  <input
                    type="text"
                    value={formData.outcome_es}
                    onChange={(e) => setFormData({ ...formData, outcome_es: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">What's Inside (EN) * - One item per line</label>
                  <textarea
                    value={formData.whats_inside}
                    onChange={(e) => setFormData({ ...formData, whats_inside: e.target.value })}
                    rows={4}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota resize-none"
                    placeholder="Event brief template&#10;Vendor matrix&#10;Guest experience map"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">What's Inside (ES)</label>
                  <textarea
                    value={formData.whats_inside_es}
                    onChange={(e) => setFormData({ ...formData, whats_inside_es: e.target.value })}
                    rows={4}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota resize-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Who It's For (EN) *</label>
                  <input
                    type="text"
                    value={formData.who_its_for}
                    onChange={(e) => setFormData({ ...formData, who_its_for: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Format *</label>
                  <input
                    type="text"
                    value={formData.format}
                    onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    placeholder="e.g., PDF + Notion template"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-stone-400 text-sm mb-1">Featured Image URL</label>
                <input
                  type="url"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                  placeholder="https://..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 rounded bg-stone-700 border-stone-600 text-terracota focus:ring-terracota"
                />
                <label htmlFor="active" className="text-stone-300 text-sm">Active (visible in store)</label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-stone-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-stone-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-terracota text-white rounded-lg hover:bg-terracota/90 transition-colors"
                >
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
